import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Upload,
  FileImage,
  FileText,
  Camera,
  CheckCircle2,
  Loader2,
  Users,
} from 'lucide-react';
import { ScheduledCase } from '@/lib/templates/types';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface StaffUploadDialogProps {
  patient: ScheduledCase | null;
  open: boolean;
  onDocsUploaded: (patientId: string, docs: { type: string; status: 'verified' | 'pending' }[]) => void;
  onCancel: () => void;
}

type StaffStep = 'upload' | 'processing' | 'done';

export function StaffUploadDialog({ patient, open, onDocsUploaded, onCancel }: StaffUploadDialogProps) {
  const [step, setStep] = useState<StaffStep>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; type: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setStep('upload');
      setUploadedFiles([]);
      setIsDragging(false);
    }
  }, [open]);

  const processFiles = useCallback((files: { name: string; type: string }[]) => {
    setUploadedFiles(files);
    setStep('processing');
    setTimeout(() => setStep('done'), 1500);
  }, []);

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).map(f => ({ name: f.name, type: f.type }));
    if (files.length > 0) processFiles(files);
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files).map(f => ({ name: f.name, type: f.type })) : [];
    if (files.length > 0) processFiles(files);
  };

  const handleSave = () => {
    if (!patient) return;
    const docs = uploadedFiles.map(f => {
      // Derive doc type from filename
      const lower = f.name.toLowerCase();
      let docType = 'Clinical Document';
      if (lower.includes('mri')) docType = 'MRI Report';
      else if (lower.includes('pt') || lower.includes('therapy')) docType = 'PT Notes';
      else if (lower.includes('xray') || lower.includes('x-ray')) docType = 'X-Ray Report';
      else if (lower.includes('face') || lower.includes('sheet')) docType = 'Face Sheet';
      return { type: docType, status: 'verified' as const };
    });
    onDocsUploaded(patient.id, docs);
    toast.success('Documents saved', {
      description: `${docs.length} document(s) pre-loaded for ${patient.patient_name}`,
    });
  };

  if (!patient) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-lg">
        {step === 'upload' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                Staff Pre-Chart Upload
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50/60 border border-amber-200">
                <User className="w-4 h-4 text-amber-700 shrink-0" />
                <span className="text-sm font-medium">{patient.patient_name}</span>
                <span className="text-xs text-muted-foreground">•</span>
                <Badge variant="secondary" className="text-[10px]">{patient.scheduled_procedure || 'Procedure TBD'}</Badge>
              </div>

              <p className="text-xs text-muted-foreground">
                Upload clinical documents now so they're ready when the surgeon reviews this case.
              </p>

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'relative rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer',
                  isDragging
                    ? 'border-amber-500 bg-amber-50/80 scale-[1.01]'
                    : 'border-amber-300 bg-white hover:border-amber-500 hover:bg-amber-50/50',
                )}
              >
                <input ref={fileInputRef} type="file" className="hidden" accept="application/pdf,.pdf,image/*,.doc,.docx,.pages,.rtf,.txt" multiple onChange={handleFileSelect} />
                <div className="flex flex-col items-center gap-3 py-10 px-4 text-center">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Camera className="w-5 h-5 text-amber-600" /></div>
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><FileImage className="w-5 h-5 text-amber-600" /></div>
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><FileText className="w-5 h-5 text-amber-600" /></div>
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    {isDragging ? 'Drop files here…' : 'Drag & drop or click to upload clinical documents'}
                  </p>
                  <p className="text-xs text-muted-foreground">MRI Reports, PT Notes, X-Rays, Face Sheets</p>
                  <Button variant="outline" size="sm" type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                    <Upload className="w-4 h-4 mr-1" />Browse Files
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onCancel}>Cancel</Button>
            </DialogFooter>
          </>
        )}

        {step === 'processing' && (
          <div className="flex flex-col items-center justify-center gap-4 py-14 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
              <Loader2 className="w-7 h-7 text-amber-600 animate-spin" />
            </div>
            <p className="text-sm font-semibold text-foreground">Saving documents to patient chart…</p>
            <p className="text-xs text-muted-foreground">{uploadedFiles.map(f => f.name).join(', ')}</p>
          </div>
        )}

        {step === 'done' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                Documents Pre-Loaded
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
                <p className="text-xs font-semibold text-emerald-800">
                  {uploadedFiles.length} document(s) saved for {patient.patient_name}
                </p>
                <ul className="space-y-1.5">
                  {uploadedFiles.map((f) => (
                    <li key={f.name} className="flex items-center gap-2 text-sm text-emerald-700">
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                      {f.name}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-muted-foreground">
                These documents will be available when the surgeon reviews this patient.
              </p>
            </div>

            <DialogFooter>
              <Button onClick={handleSave}>
                Save & Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
